import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { DocuSignConfig, DocuSignRequest, SignerViewResult } from './models/docusign-models';


const docuSignTokenProxyUrl = '/api/v1/docusign/getaccesstoken';
const sdkString = 'DocuSign-eSignature-SDK-Node-React/3.0.0';
const apiVersion = '/restapi/v2.1';

function prepareEnvelope(request: DocuSignRequest): object {

    if (request?.rawEnvelope) return request?.rawEnvelope;
        
    const additionalTextCustomFields = request.additionalTextCustomFields || [];

    const textCustomFields = [
        {
            name: 'TemplateID',
            value: request?.customFieldTemplateId,
        },
        {
            name: 'ClientId',
            value: request?.customFieldClientId,
        },
        ...additionalTextCustomFields,
    ];

    return {
        status: 'sent',
        emailSubject: request?.subject,        
        compositeTemplates: [
            {
                compositeTemplateId: '1',
                serverTemplates: [
                    {
                        sequence: 1,
                        templateId: request?.templateId,
                    },
                ],
                inlineTemplates: [
                    {
                        sequence: 2,
                        recipients: {
                            signers: [
                                {
                                    clientUserId: request?.signerClientId,
                                    recipientId: '1',
                                    roleName: request?.signerRoleName,
                                    name: request?.signerName,
                                    email: request?.signerEmail,
                                    tabs: request?.tabs,                                    
                                },
                            ],
                        },
                        customFields: {
                            textCustomFields,
                        },
                    },
                ],
            },
        ],
    };
}

function createDocuSignEnvelopeAsync(docuSignConfig: DocuSignConfig, accessToken: string, envelopeRequest: object): Promise<string> {

    const { docuSignAppBaseUri, accountId } = docuSignConfig;

    return new Promise<string>(
        // eslint-disable-next-line no-async-promise-executor
        async (resolve, reject) => {
            try {
                const url = `${docuSignAppBaseUri}${apiVersion}/accounts/${accountId}/envelopes`;
                const response = await fetch(url, {
                    method: 'POST',
                    body: JSON.stringify(envelopeRequest),
                    headers: new Headers({
                        Authorization: `Bearer ${accessToken}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-DocuSign-SDK': sdkString,
                    }),
                });

                if (!response?.ok) {                    
                    reject(new Error('Error creating envelope'));
                    return;
                }

                const data = await response.json();

                resolve(data?.envelopeId);
            } catch (error) {
                reject(error);
            }
        });
}

function createRecipientViewDefinition(request: DocuSignRequest, docuSignConfig: DocuSignConfig): object {

    if (request?.rawRecipientViewDefinition) return request?.rawRecipientViewDefinition;

    const dateTime = new Date(Number(request?.loginTime) * 1000);

    const requestData = {
        assertionId: request.authToken,
        authenticationInstant: dateTime.toISOString(),
        returnUrl: `${docuSignConfig.siteBaseUri}/services`,
        authenticationMethod: 'SingleSignOn_Other',
        securityDomain: docuSignConfig.siteBaseUri,
        email: request?.signerEmail,
        userName: request?.signerName,
        clientUserId: request?.signerClientId,
        frameAncestors: [docuSignConfig.siteBaseUri, docuSignConfig.docuSignAppFrameAncestorUri],
        messageOrigins: [docuSignConfig.docuSignAppFrameAncestorUri],
    };

    return requestData;
}

function createRecipientViewAsync(docuSignConfig: DocuSignConfig, accessToken: string, envelopeId: string, recipientRequest: object): Promise<object> {

    const { docuSignAppBaseUri, accountId } = docuSignConfig;

    return new Promise<object>(
        // eslint-disable-next-line no-async-promise-executor
        async (resolve, reject) => {
            try {
                const url = `${docuSignAppBaseUri}${apiVersion}/accounts/${accountId}/envelopes/${envelopeId}/views/recipient`;
                const response = await fetch(url, {
                    method: 'POST',
                    body: JSON.stringify(recipientRequest),
                    headers: new Headers({
                        Authorization: `Bearer ${accessToken}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-DocuSign-SDK': sdkString,
                    }),
                });

                if (!response?.ok) {
                    reject(new Error('Error creating recipient view'));                    
                    return;
                }

                const data = await response.json();

                resolve(data);
            } catch (error) {
                reject(error);
            }
        });
}

export async function GetSignatureUrl(request: DocuSignRequest, docuSignConfig: DocuSignConfig, onSignerViewSucess?: (request: SignerViewResult) => void, onFailure?: () => void) {

    if (!request || !docuSignConfig) {
        onFailure();
        return;
    }

    try {
        const serverTokenResponse = await fetch(docuSignTokenProxyUrl);
        const token = await serverTokenResponse.json();

        const envelope = prepareEnvelope(request);

        const envelopeId = await createDocuSignEnvelopeAsync(docuSignConfig, token?.access_token, envelope);

        const recipientViewDefinition = createRecipientViewDefinition(request, docuSignConfig);

        const viewResponse: any = await createRecipientViewAsync(docuSignConfig, token?.access_token, envelopeId, recipientViewDefinition);

        if (onSignerViewSucess) {
            const completedRequest: SignerViewResult = {
                envelopeId,
                signatureUrl: viewResponse?.url,
            };

            onSignerViewSucess(completedRequest);
        }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);

        if (onFailure) onFailure();
    }
}

export async function GetSignedDocument(envelopId: string, docuSignConfig: DocuSignConfig) {
    const serverTokenResponse = await fetch(docuSignTokenProxyUrl);
    const token = await serverTokenResponse.json();
    const header = new Headers({
        Authorization: `Bearer ${token?.access_token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
    });

    return new Promise<Blob>(
        // eslint-disable-next-line no-async-promise-executor
        async (resolve, reject) => {
            try {
                const url = `${docuSignConfig.docuSignAppBaseUri}${apiVersion}/accounts/${docuSignConfig.accountId}/envelopes/${envelopId}/documents/archive`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: header,
                });

                if (!response?.ok) {
                    reject(new Error('Error Downloading Signing Doc'));
                    return;
                }

                const blob = await response.blob();

                resolve(blob);
            } catch (error) {
                reject(error);
            }
        });

}

export function UnzipAndDownloadPdf(blob: Blob) {
    const zip = new JSZip();

    // Load the blob file asynchronously
    zip.loadAsync(blob).then((zipFile) => {
        // Extract files from the zip archive
        zipFile.forEach((relativePath, file) => {
            if (!relativePath?.includes('Summary.pdf')) {
                file.async('blob').then((content) => {
                    // Save each file to disk
                    saveAs(content, relativePath);
                });
            }
        });
    });

}